import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import InstagramLogin from '../../components/Onboarding/InstagramLogin.tsx';
import { createSocial } from '../../api/social.ts';
import { setOnboardStatus } from '../../api/user.ts';
import Loader from '../../components/Loader.tsx';
import Logo from '../../assets/logo.png';
import ProgressIndicator from '../../components/Onboarding/ProgressIndicatorComponent.tsx';
import useCustomWebSocket from '../../hooks/useWebSocket.tsx';
import { PlatformType, useSocial } from '../../context/socialProvider.tsx';
import {
  useOnboardingTracking,
  ONBOARDING_STEPS,
} from '../../hooks/use-onboarding-tracking';

const Onboarding = () => {
  type SocialAccount = {
    id: string;
    providerName: string;
    provider: number; // 1 for Instagram, 2 for Facebook
    profilePictureUrl: string;
  };

  const [currentStep, setCurrentStep] = useState(1);
  const [instagramToken, setInstagramToken] = useState('');
  const [instagramError, setInstagramError] = useState(false);
  const [socialAccounts, setSocialAccounts] = useState<SocialAccount[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [testComment, setTestComment] = useState('');
  const [isTestingComment, setIsTestingComment] = useState(false);
  const [testCommentResult, setTestCommentResult] = useState<{
    evaluation: boolean;
    reasoning: string;
  } | null>(null);
  const resultRef = useRef<HTMLDivElement>(null);

  const [selectedInstagramAccounts, setSelectedInstagramAccounts] = useState<
    string[]
  >([]);
  const [selectedFacebookAccounts, setSelectedFacebookAccounts] = useState<
    string[]
  >([]);

  const [unifiedSettings, setUnifiedSettings] = useState({
    autoModActive: true,
    moderationInstructions: '',
    toxicityScores: {},
  });

  const [autoSaveStatus, setAutoSaveStatus] = useState('');
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const navigate = useNavigate();

  const { sendWebSocketMessage, lastMessage } = useCustomWebSocket();
  const { refreshSocialInfo } = useSocial();

  // Get userId for tracking
  const userId = localStorage.getItem('userId');

  // Determine user name for tracking - use account info when available
  const userName =
    socialAccounts.length > 0 ? socialAccounts[0].providerName : 'New User';

  // Initialize tracking
  const { trackAction, trackAbandonment } = useOnboardingTracking({
    currentStep: currentStep as keyof typeof ONBOARDING_STEPS,
    userId: userId || undefined,
    currentSocial: socialAccounts,
  });

  // Track page view when component mounts
  useEffect(() => {
    // Track abandonment when user leaves the page without completing
    return () => {
      if (currentStep < 11) {
        // Only track abandonment if onboarding is not complete
        trackAbandonment('Navigation away from onboarding');
      }
    };
  }, []);

  // Filter accounts by provider
  const instagramAccounts = socialAccounts.filter(
    (account) => account.provider === 1
  );
  const facebookAccounts = socialAccounts.filter(
    (account) => account.provider === 2
  );
  const metaAdAccount = socialAccounts.filter(
    (account) => account.provider === 3
  );

  const continueToDashboard = async () => {
    try {
      trackAction('Continue To Dashboard');
      await setOnboardStatus();
      await refreshSocialInfo();
      trackAction('Onboarding Successfully Completed');
      navigate(`/global/settings`);
    } catch (error: any) {
      trackAction('Dashboard Navigation Failed', {
        error: error === 401 ? 'Unauthorized' : 'Unknown error',
      });

      if (error === 401) {
        console.log('unauthorized setting onboard status');
      } else {
        console.log('set onboarding error');
      }
    }
  };

  const prevStep = () => {
    if (currentStep > 4) {
      trackAction('Previous Step Clicked');
      setCurrentStep(currentStep - 1);
    }
  };

  useEffect(() => {
    console.log('IS LOADING STATE:', isLoading);
  }, [isLoading]);

  useEffect(() => {
    const onboardingFinished = async () => {
      await refreshSocialInfo(); //refresh platform context and user onboarded status
      console.log('refreshed');
    };

    if (lastMessage?.data) {
      const data = JSON.parse(lastMessage.data);
      if (data.action === 'set_main_status') {
        switch (data.payload.status) {
          case 'process':
            setIsLoading(true);
            trackAction('Main Social Account Setting In Progress');
            break;
          // Optional progress loading (based off provider)
          case 'complete':
            console.log('complete from websocket');
            trackAction('Main Social Account Setting Completed');
            onboardingFinished();
            setIsLoading(false);
            break;
          case 'error':
            setIsLoading(false);
            trackAction('Main Social Account Setting Failed', {
              error: data.payload.error,
            });
            console.error('websocket error', data.payload.error);
            break;
        }
      }
    }
  }, [lastMessage]);

  useEffect(() => {
    // Check for access token in URL hash (Facebook OAuth redirect)
    const hash = window.location.hash;
    if (hash.includes('access_token')) {
      // Extract the token from the URL hash
      const tokenMatch = hash.match(/access_token=([^&]*)/);
      if (tokenMatch && tokenMatch[1]) {
        const accessToken = tokenMatch[1];
        console.log('Facebook access token obtained');

        // Save the token to localStorage
        localStorage.setItem('token', accessToken);
        console.log('Token saved to localStorage');

        // Remove the hash from the URL to prevent token leakage
        window.history.replaceState(
          null,
          '',
          window.location.pathname + window.location.search
        );

        // Force move to step 2 if not already there
        if (currentStep < 2) {
          setIsLoading(true);
          setCurrentStep(2);
        }
      }
    }
  }, []);

  useEffect(() => {
    const fetchAndSetTokens = async () => {
      const token = localStorage.getItem('token');
      console.log('token', token);
      if (!token) {
        trackAction('Token Not Found', { step: 'fetchAndSetTokens' });
        console.log('No token lol');
      } else {
        const userId = localStorage.getItem('userId');
        trackAction('Token Found', { step: 'fetchAndSetTokens' });
        console.log('Yay get in', userId, token);
        await createInstagramSocial(userId, token);
      }
    };

    const createInstagramSocial = async (userId: any, token: any) => {
      console.log('instagramlong', token);
      trackAction('Creating Instagram Social Account', {
        step: 'createInstagramSocial',
      });

      await createSocial(token)
        .then((accounts) => {
          console.log(accounts.data);
          setSocialAccounts(accounts.data);
          trackAction('Instagram Social Account Created', {
            accounts_count: accounts.data.length,
          });
          console.log('setIsloading false createinstasocial');
          setIsLoading(false);
          setCurrentStep(3);
        })
        .catch((error) => {
          trackAction('Instagram Social Account Creation Failed', {
            error: error.message || 'Unknown error',
          });
          console.error('Failed to create social accounts', error);
        });
    };

    const searchParams = new URLSearchParams(location.search);
    const step = searchParams.get('step');
    const errorDenied = searchParams.get('error');
    if (errorDenied === 'access_denied') {
      trackAction('Meta Authentication Denied', { error: 'access_denied' });
      setInstagramError(true);
      setCurrentStep(2);
    } else if (step === '2') {
      trackAction('Moving to Step 2 via URL', { from_step: currentStep });
      setIsLoading(true);
      setCurrentStep(2);
      fetchAndSetTokens();
    }
  }, [location.search]);

  const nextStep = (e: any) => {
    trackAction('Next Step Clicked', {
      from_step: currentStep,
      to_step: currentStep + 1,
    });
    setCurrentStep(currentStep + 1);
  };

  const toggleAccountSelection = (
    platform: 'instagram' | 'facebook',
    accountId: string
  ) => {
    if (platform === 'instagram') {
      setSelectedInstagramAccounts((prev) =>
        prev.includes(accountId)
          ? prev.filter((id) => id !== accountId)
          : [...prev, accountId]
      );
    } else {
      setSelectedFacebookAccounts((prev) =>
        prev.includes(accountId)
          ? prev.filter((id) => id !== accountId)
          : [...prev, accountId]
      );
    }
  };

  const confirmSocialAccounts = async () => {
    if (
      selectedInstagramAccounts.length === 0 &&
      selectedFacebookAccounts.length === 0
    ) {
      // Show error or prevent proceeding
      return;
    }

    trackAction('Confirm Social Accounts', {
      instagram_accounts: selectedInstagramAccounts,
      facebook_accounts: selectedFacebookAccounts,
    });

    setIsLoading(true);
    const accounts: any = [];
    try {
      const userId = localStorage.getItem('userId');
      if (userId) {
        // Send WebSocket message for each Instagram account
        selectedInstagramAccounts.forEach((accountId) => {
          accounts.push({
            provider: PlatformType.INSTAGRAM,
            socialId: accountId,
          });
        });

        // Send WebSocket message for each Facebook account
        selectedFacebookAccounts.forEach((accountId) => {
          accounts.push({
            provider: PlatformType.FACEBOOK,
            socialId: accountId,
          });
        });

        sendWebSocketMessage('set_main', {
          accounts: accounts,
        });

        trackAction('Social Accounts Configuration Sent', {
          instagram_account_count: selectedInstagramAccounts.length,
          facebook_account_count: selectedFacebookAccounts.length,
        });

        await refreshSocialInfo();
        setIsLoading(false);
        nextStep(null);
      }
    } catch (error: any) {
      trackAction('Social Account Configuration Failed', {
        error: error.message || 'Unknown error',
      });
      console.error('failed to set accounts', error);
      setIsLoading(false);
    }
  };

  // automod
  const [autoModSettings, setAutoModSettings] = useState({
    hideTypes: [] as string[],
    nlpHidden: '',
    nlpShown: '',
    moderateLinks: false,
  });
  const [showAutoModResults, setShowAutoModResults] = useState(false);

  // Add these handlers before the renderStepContent function
  const handleCheckboxChange = (type: string) => {
    const isSelected = autoModSettings.hideTypes.includes(type);
    trackAction(
      isSelected ? 'Content Type Unselected' : 'Content Type Selected',
      { type }
    );

    setAutoModSettings((prev) => {
      const newHideTypes = prev.hideTypes.includes(type)
        ? prev.hideTypes.filter((t) => t !== type)
        : [...prev.hideTypes, type];
      return { ...prev, hideTypes: newHideTypes };
    });
  };

  const handleInputChange = (
    field: string,
    value: string | number | boolean
  ) => {
    trackAction('AutoMod Setting Changed', { field, value });
    setAutoModSettings((prev) => ({ ...prev, [field]: value }));
  };

  const renderSocialAccountSelectors = () => {
    return (
      <div className="flex flex-row gap-6 w-full max-w-2xl">
        {/* Instagram Accounts Column */}
        <div className="flex-1 bg-grey bg-opacity-20 rounded-lg p-4 max-h-72 overflow-y-auto">
          <h3 className="text-lg font-semibold mb-3 text-center">Instagram</h3>
          {instagramAccounts.length > 0 ? (
            instagramAccounts.map((account) => (
              <div
                key={account.id}
                className="flex items-center mb-3 last:mb-0"
              >
                <input
                  type="checkbox"
                  id={`instagram-${account.id}`}
                  checked={selectedInstagramAccounts.includes(account.id)}
                  onChange={() =>
                    toggleAccountSelection('instagram', account.id)
                  }
                  className="mr-3 h-4 w-4 text-violet focus:ring-violet"
                />
                <label
                  htmlFor={`instagram-${account.id}`}
                  className="flex items-center cursor-pointer w-full"
                >
                  <img
                    src={account.profilePictureUrl}
                    alt={account.providerName}
                    className="w-10 h-10 rounded-full mr-3 object-cover border border-gray-400"
                  />
                  <span className="truncate">@{account.providerName}</span>
                </label>
              </div>
            ))
          ) : (
            <div className="text-center text-white text-opacity-70 py-8">
              No Instagram accounts found.
            </div>
          )}
        </div>

        {/* Facebook Accounts Column */}
        <div className="flex-1 bg-grey bg-opacity-20 rounded-lg p-4 max-h-72 overflow-y-auto">
          <h3 className="text-lg font-semibold mb-3 text-center">Facebook</h3>
          {facebookAccounts.length > 0 ? (
            facebookAccounts.map((account) => (
              <div
                key={account.id}
                className="flex items-center mb-3 last:mb-0"
              >
                <input
                  type="checkbox"
                  id={`facebook-${account.id}`}
                  checked={selectedFacebookAccounts.includes(account.id)}
                  onChange={() =>
                    toggleAccountSelection('facebook', account.id)
                  }
                  className="mr-3 h-4 w-4 text-violet focus:ring-violet"
                />
                <label
                  htmlFor={`facebook-${account.id}`}
                  className="flex items-center cursor-pointer w-full"
                >
                  <img
                    src={
                      account.profilePictureUrl ||
                      'https://superpower-alpha.s3.amazonaws.com/default_images/no_pfp.jpg'
                    }
                    alt={account.providerName}
                    className="w-10 h-10 rounded-full mr-3 object-cover border border-gray-400"
                  />
                  <span className="truncate">@{account.providerName}</span>
                </label>
              </div>
            ))
          ) : (
            <div className="text-center text-white text-opacity-70 py-8">
              No Facebook accounts found.
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderStepContent = (currentStep: any) => {
    switch (currentStep) {
      case 1:
        return (
          <div>
            <h1 className="text-3xl mt-4 justify-left">Welcome ✌️</h1>
            <div className="flex flex-col w-full max-w-xl text-center my-4">
              <span className="text-left text-xl font-thin mb-2">
                There are thousands of brands out there, <br />
                and you're about to join the smart ones. <br />
                <br />
                Superpower is your secret weapon in the battle against social
                media engagement overload.
              </span>
            </div>
            <div className="flex flex-row justify-center">
              <button
                className="bg-yellow text-black rounded-3xl p-2 my-6 w-60 hover:brightness-90"
                onClick={nextStep}
              >
                {`Continue >`}
              </button>
            </div>
          </div>
        );
      case 2:
        return (
          <div className="flex flex-col items-center mx-auto">
            <h1 className="text-xl my-4 text-center">Connect Meta account</h1>
            {instagramError && (
              <p className="text-red text-xs pb-4">
                That didn't work, try again or hit us up at{' '}
                <a href="mailto:hello@superpower.social" className="underline">
                  hello@superpower.social
                </a>
              </p>
            )}
            {isLoading ? <Loader stages={loadingStages} /> : <InstagramLogin />}
            {/* <span className="text-md font-thin mt-6 mb-3">
              Superpower Social is a Meta-approved app
            </span>

            <img
              src={MetaApprovedBadge}
              alt="Meta Approved App"
              className="w-[100px] h-auto"
            /> */}
          </div>
        );
      case 3:
        return (
          <div className="flex flex-col items-center mx-auto">
            <h1 className="text-xl my-4">Connect Social Accounts</h1>
            {isLoading ? (
              <Loader stages={loadingStages2} />
            ) : (
              <>
                <span className="text-xs mb-4 text-center max-w-md">
                  Select one or more social media accounts you'd like to manage.
                  You can always add or remove accounts later.
                </span>

                {renderSocialAccountSelectors()}

                <button
                  className={`bg-yellow text-black rounded-3xl p-2 my-6 w-60 hover:brightness-90 
                      ${
                        selectedInstagramAccounts.length +
                          selectedFacebookAccounts.length >
                        0
                          ? 'opacity-100'
                          : 'opacity-50 cursor-not-allowed'
                      }`}
                  onClick={confirmSocialAccounts}
                  disabled={
                    selectedInstagramAccounts.length +
                      selectedFacebookAccounts.length ===
                    0
                  }
                >
                  {`Continue >`}
                </button>
              </>
            )}
          </div>
        );
      case 4:
        return (
          <div className="flex flex-col items-center w-full">
            <h1 className="text-xl my-4">A quick guide to using Superpower</h1>
            <iframe
              width="480"
              height="270"
              src="https://www.youtube.com/embed/FGt-HgByR8o?si=YMRY4jGDpbms67XW"
              title="Onboardingvideonew"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              referrerPolicy="strict-origin-when-cross-origin"
              allowFullScreen
            ></iframe>

            <button
              className="bg-yellow text-black rounded-2xl p-2 mt-8 mb-2 w-80 hover:brightness-90"
              onClick={continueToDashboard}
            >
              {'Go to Dashboard >'}
            </button>
          </div>
        );
    }
  };

  return (
    <div className="flex flex-col items-center justify-center h-screen relative">
      <div className="absolute top-16 left-16 z-10">
        <img src={Logo} alt="Super Power" className="w-24 h-auto" />
      </div>

      {/* Progress indicator showing current step */}
      <ProgressIndicator currentStep={currentStep} totalSteps={4} />

      {/* Conditionally render based on current step */}
      {currentStep === 9 || currentStep === 3 ? (
        // Special full-width layout for AutoMod Configuration (Step 10)
        <div className="flex bg-black py-8 px-8 rounded-3xl m-5 w-full max-w-5xl">
          {renderStepContent(currentStep)}
        </div>
      ) : (
        // Standard layout for all other steps
        <div className="flex bg-grey py-10 px-28 rounded-3xl m-5 w-[640px]">
          {renderStepContent(currentStep)}
        </div>
      )}
    </div>
  );
};

const loadingStages = [
  {
    interval: 10000, // 5 seconds
    svg: (
      <svg
        aria-hidden="true"
        className="w-32 h-32 text-lightgrey animate-spin dark:text-gray-600 fill-violet"
        viewBox="0 0 100 101"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
          fill="currentColor"
        />
        <path
          d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
          fill="currentFill"
        />
      </svg>
    ),
    text: 'Loading your accounts...',
  },
];

// we can make this better later
const loadingStages2 = [
  {
    interval: 10000, // 5 seconds
    svg: (
      <svg
        aria-hidden="true"
        className="w-32 h-32 text-lightgrey animate-spin dark:text-gray-600 fill-violet"
        viewBox="0 0 100 101"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
          fill="currentColor"
        />
        <path
          d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
          fill="currentFill"
        />
      </svg>
    ),
    text: 'Fetching data... this will take a minute ⏰',
  },
];

export default Onboarding;
